import React, {FC, useCallback, useEffect, useMemo} from "react";
import {Cluster, ClusterStats, type Marker, MarkerClusterer} from '@googlemaps/markerclusterer';
import {useMap} from "@vis.gl/react-google-maps";
import LocationMarker from "./LocationMarker";
import type {Supplier} from "../../../types/Supplier";
import createLocationCluster from "./CreateLocationCluster";
import {useTheme} from "@mui/material";
import {useMarkerRefs} from "../../../hooks/useMarkerRefs";

interface IClusteredLocationMarkers {
  locations: Supplier[];
  onClick: (location: Supplier) => void;
  hoveredLocation?: Supplier | null | undefined;
  highlightedLocation?: Supplier | null;
  setHighlightedLocation: (location: Supplier | null) => void;
}

/**
 * Reference: https://github.com/visgl/react-google-maps/blob/main/examples/marker-clustering/src/clustered-tree-markers.tsx
 * @param locations
 * @param onClick
 * @param hoveredLocation
 * @param highlightedLocation
 * @param setHighlightedLocation
 * @constructor
 */
const ClusteredLocationMarkers: FC<IClusteredLocationMarkers> = ({
  locations,
  onClick,
  hoveredLocation,
  highlightedLocation,
  setHighlightedLocation
}) => {
  const {markers, setMarkerRef} = useMarkerRefs<Marker>();

  const theme = useTheme();
  // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap();

  const clusterer = useMemo(() => {
    if (!map) return null;
    return new MarkerClusterer({
      algorithmOptions: { maxZoom: 15 },
      map,
      renderer: {
        render(cluster: Cluster, _stats: ClusterStats, _map: google.maps.Map): Marker {
          // Create a DOM container and render the React component into it
          // if the majority of markers is true for hasInstantApproval, then we show the instant approval icon
          const markers = cluster.markers;
          const instantCount = markers.filter(
            marker => (marker as any).hasInstantApproval === true
          ).length;
          const majorityInstant = instantCount > (markers.length / 2);
          return new google.maps.marker.AdvancedMarkerElement({
            position: cluster.position,
            content: createLocationCluster(majorityInstant ? theme.palette.success.main : theme.palette.warning.main),
          })
        }
      }
    });
  }, [map, theme]);

  useEffect(() => {
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  const handleMarkerClick = useCallback((location: Supplier) => {
    onClick(location)
  }, [onClick]);

  return locations.map((loc: Supplier) => (
    <LocationMarker
      key={loc.locationId}
      location={loc}
      hover={hoveredLocation?.locationId === loc.locationId}
      onLocationSelect={handleMarkerClick}
      setMarkerRef={setMarkerRef}
      highlightedLocation={highlightedLocation}
      setHighlightedLocation={setHighlightedLocation}
    />
  ));
}

export default ClusteredLocationMarkers;
